import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button/Button';
import { Container } from '../ui/Container/Container';
import { useAuthStore } from '../../stores/authStore';
import { UserMenu } from './UserMenu';
import { Logo } from '../ui/Logo';

interface StandardHeaderProps {
	/** Optional step indicator to show next to logo (for storybook creation flow) */
	stepLabel?: string;
	/** Show Dashboard and Pricing buttons (for homepage) */
	showNavigation?: boolean;
	/** Container size - defaults to 'lg' */
	containerSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
	/** Use transparent/dark variant (for dark hero sections) */
	transparent?: boolean;
	/** Overlay mode — header sits absolutely over page content (for landing hero) */
	overlay?: boolean;
}

export function StandardHeader({ stepLabel, showNavigation = false, containerSize = 'lg', transparent = false, overlay = false }: StandardHeaderProps) {
	const { user } = useAuthStore();
	const fetchMe = useAuthStore((s) => s.fetchMe);
	const attemptedHydration = useRef(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const location = useLocation();

	useEffect(() => {
		if (attemptedHydration.current) return;
		attemptedHydration.current = true;
		if (!useAuthStore.getState().user) {
			(async () => {
				try {
					await fetchMe(true);
				} catch (e) {
					// Silent fail - user is not logged in
				}
			})();
		}
	}, [fetchMe]);

	// Close mobile menu on route change
	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	// Lock body scroll when mobile menu is open
	useEffect(() => {
		if (mobileOpen) {
			document.body.style.overflow = 'hidden';
			return () => { document.body.style.overflow = ''; };
		}
	}, [mobileOpen]);

	const toggleMenu = useCallback(() => setMobileOpen(prev => !prev), []);

	const position = overlay ? 'fixed top-0 left-0 right-0 z-50' : 'sticky top-0 z-50';
	const borderClass = overlay ? '' : 'border-b';

	const headerClasses = transparent
		? `h-14 sm:h-16 flex items-center justify-between border-b border-white/[0.06] ${position}`
		: `h-14 sm:h-16 flex items-center justify-between ${borderClass} ${position}`;

	const headerStyle: React.CSSProperties = transparent
		? { background: 'rgba(3,7,17,0.72)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)' }
		: overlay
			? { background: 'rgba(255,255,255,0.35)', backdropFilter: 'blur(24px) saturate(1.6)', WebkitBackdropFilter: 'blur(24px) saturate(1.6)' }
			: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(24px) saturate(1.6)', WebkitBackdropFilter: 'blur(24px) saturate(1.6)', borderColor: 'rgba(0,0,0,0.06)' };

	const logoClasses = transparent
		? 'font-bold text-white'
		: 'font-bold text-gray-800';

	const ghostButtonClasses = transparent
		? 'text-gray-300 hover:text-white'
		: '';

	/* Navigation links — shared between desktop and mobile */
	const navLinks = showNavigation ? (
		user
			? [
				{ to: '/dashboard', label: 'Dashboard' },
				{ to: '/blog', label: 'Blog' },
				{ to: '/pricing', label: 'Pricing' },
			]
			: [
				{ to: '/blog', label: 'Blog' },
				{ to: '/pricing', label: 'Pricing' },
			]
	) : [];

	return (
		<>
			<header className={headerClasses} style={headerStyle}>
				<Container size={containerSize} className="flex items-center justify-between px-4 md:px-6">
					{/* Logo + step label */}
					<div className="flex items-center gap-4">
						<Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
							<Logo size="md" forceWhite={transparent} />
						</Link>
						{stepLabel && (
							<>
								<div className={`h-6 w-px ${transparent ? 'bg-white/20' : 'bg-magic-300'}`} />
								<div className={`text-xs font-semibold uppercase tracking-wider ${transparent ? 'text-gray-400' : 'text-magic-400'}`}>
									{stepLabel}
								</div>
							</>
						)}
					</div>

					{/* Desktop navigation */}
					<div className="hidden sm:flex items-center gap-3">
						{navLinks.map(link => (
							<Link key={link.to} to={link.to}>
								<Button variant="ghost" size="sm" className={ghostButtonClasses}>
									{link.label}
								</Button>
							</Link>
						))}
						{user ? (
							<UserMenu />
						) : (
							<Link to="/auth">
								{transparent ? (
									<Button variant="secondary" size="sm" className="bg-white/10 text-white border-white/20 hover:bg-white/20">Sign in</Button>
								) : (
									<button
										className="px-5 py-2 rounded-full text-[13px] font-semibold transition-all hover:scale-[1.03] active:scale-[0.97]"
										style={{
											background: '#1d2939',
											color: '#fff',
											boxShadow: '0 2px 8px rgba(29,41,57,0.2)',
											fontFamily: 'Inter,system-ui,sans-serif',
											letterSpacing: '-0.01em',
										}}
									>
										Sign in
									</button>
								)}
							</Link>
						)}
					</div>

					{/* Mobile: Sign in + Hamburger */}
					<div className="flex sm:hidden items-center gap-2">
						{!user && (
							<Link to="/auth">
								<button
									className="px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all active:scale-[0.96]"
									style={{
										background: '#1d2939',
										color: '#fff',
										fontFamily: 'Inter,system-ui,sans-serif',
									}}
								>
									Sign in
								</button>
							</Link>
						)}
						{(showNavigation || user) && (
							<button
								onClick={toggleMenu}
								className="relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors"
								style={{ background: mobileOpen ? 'rgba(29,41,57,0.08)' : 'transparent' }}
								aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
								aria-expanded={mobileOpen}
							>
								<div className="w-5 h-4 relative flex flex-col justify-between">
									{/* Animated hamburger → X morphing */}
									<span
										className="block h-[1.5px] rounded-full transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)]"
										style={{
											width: mobileOpen ? 20 : 18,
											background: transparent ? '#fff' : '#1d2939',
											transform: mobileOpen ? 'translateY(7px) rotate(45deg)' : 'none',
											transformOrigin: 'center',
										}}
									/>
									<span
										className="block h-[1.5px] rounded-full transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)]"
										style={{
											width: 14,
											background: transparent ? '#fff' : '#1d2939',
											opacity: mobileOpen ? 0 : 1,
											transform: mobileOpen ? 'translateX(-6px)' : 'none',
										}}
									/>
									<span
										className="block h-[1.5px] rounded-full transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)]"
										style={{
											width: mobileOpen ? 20 : 16,
											background: transparent ? '#fff' : '#1d2939',
											transform: mobileOpen ? 'translateY(-9px) rotate(-45deg)' : 'none',
											transformOrigin: 'center',
										}}
									/>
								</div>
							</button>
						)}
					</div>
				</Container>
			</header>

			{/* ── Mobile menu overlay ─────────────────────────── */}
			<div
				className="sm:hidden fixed inset-0 z-40"
				style={{
					pointerEvents: mobileOpen ? 'auto' : 'none',
				}}
			>
				{/* Backdrop */}
				<div
					className="absolute inset-0 transition-opacity duration-300"
					style={{
						background: 'rgba(29,41,57,0.18)',
						backdropFilter: mobileOpen ? 'blur(6px)' : 'blur(0px)',
						WebkitBackdropFilter: mobileOpen ? 'blur(6px)' : 'blur(0px)',
						opacity: mobileOpen ? 1 : 0,
					}}
					onClick={() => setMobileOpen(false)}
				/>

				{/* Menu panel — slides down from top */}
				<div
					className="absolute left-0 right-0 transition-all duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
					style={{
						top: 56, // h-14 = 56px
						transform: mobileOpen ? 'translateY(0)' : 'translateY(-12px)',
						opacity: mobileOpen ? 1 : 0,
					}}
				>
					<div
						className="mx-3 rounded-2xl overflow-hidden"
						style={{
							background: 'rgba(255,255,255,0.85)',
							backdropFilter: 'blur(32px) saturate(1.8)',
							WebkitBackdropFilter: 'blur(32px) saturate(1.8)',
							boxShadow: '0 12px 48px rgba(29,41,57,0.14), 0 2px 8px rgba(0,0,0,0.06)',
							border: '1px solid rgba(255,255,255,0.7)',
						}}
					>
						{/* Nav links */}
						<nav className="py-2">
							{navLinks.map((link, i) => (
								<Link
									key={link.to}
									to={link.to}
									className="flex items-center px-5 py-3.5 transition-colors active:bg-black/[0.03]"
									style={{
										transitionDelay: mobileOpen ? `${(i + 1) * 50}ms` : '0ms',
										opacity: mobileOpen ? 1 : 0,
										transform: mobileOpen ? 'translateX(0)' : 'translateX(-8px)',
										transition: 'opacity 300ms ease, transform 300ms ease, background-color 150ms ease',
									}}
									onClick={() => setMobileOpen(false)}
								>
									<span style={{
										color: '#1d2939',
										fontSize: 15,
										fontWeight: 500,
										fontFamily: 'Inter,system-ui,sans-serif',
										letterSpacing: '-0.01em',
									}}>
										{link.label}
									</span>
								</Link>
							))}

							{/* Divider */}
							{(navLinks.length > 0 && user) && (
								<div className="mx-5 my-1" style={{ height: 1, background: 'rgba(0,0,0,0.06)' }} />
							)}

							{/* User section if logged in */}
							{user && (
								<Link
									to="/dashboard"
									className="flex items-center gap-3 px-5 py-3.5 transition-colors active:bg-black/[0.03]"
									style={{
										transitionDelay: mobileOpen ? `${(navLinks.length + 1) * 50}ms` : '0ms',
										opacity: mobileOpen ? 1 : 0,
										transform: mobileOpen ? 'translateX(0)' : 'translateX(-8px)',
										transition: 'opacity 300ms ease, transform 300ms ease, background-color 150ms ease',
									}}
									onClick={() => setMobileOpen(false)}
								>
									{/* Avatar placeholder */}
									<div
										className="w-7 h-7 rounded-full flex items-center justify-center"
										style={{
											background: 'linear-gradient(135deg, #7ea8c9, #b5a0cc)',
										}}
									>
										<span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
											{(user as any).email?.[0]?.toUpperCase() || 'U'}
										</span>
									</div>
									<div>
										<span style={{
											color: '#1d2939',
											fontSize: 14,
											fontWeight: 500,
											fontFamily: 'Inter,system-ui,sans-serif',
											display: 'block',
										}}>
											My Dashboard
										</span>
										<span style={{
											color: '#8c95a6',
											fontSize: 12,
											fontFamily: 'Inter,system-ui,sans-serif',
											display: 'block',
											marginTop: 1,
										}}>
											{(user as any).email || 'Account'}
										</span>
									</div>
								</Link>
							)}
						</nav>

						{/* CTA row at bottom */}
						{!user && (
							<div className="px-5 pb-4 pt-1">
								<Link to="/auth" onClick={() => setMobileOpen(false)}>
									<button
										className="w-full py-3 rounded-xl text-[14px] font-semibold transition-all active:scale-[0.98]"
										style={{
											background: '#1d2939',
											color: '#fff',
											boxShadow: '0 2px 10px rgba(29,41,57,0.22)',
											fontFamily: 'Inter,system-ui,sans-serif',
											letterSpacing: '-0.01em',
											opacity: mobileOpen ? 1 : 0,
											transform: mobileOpen ? 'translateY(0)' : 'translateY(6px)',
											transition: 'opacity 350ms ease 150ms, transform 350ms ease 150ms',
										}}
									>
										Get Started Free
									</button>
								</Link>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
